import { Link } from "react-router-dom";
import { Smile, GraduationCap, ArrowRight } from "lucide-react";
import { IDS } from "@/lib/testIds";

export default function TrackCards() {
  return (
    <section id="tracks" className="relative py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
            Choose your track
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
            Two paths. One goal — <span className="text-sky-500">confident reading.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Pick the track that fits your age and pace. Every lesson has pictures,
            sounds, and words you can play out loud.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Basic */}
          <div
            data-testid={IDS.tracks.basicCard}
            className="group relative bg-white rounded-[2rem] p-8 sm:p-10 border-2 border-slate-100 hover:border-sky-200 transition-all shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 duration-300 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-sky-100 rounded-full -translate-y-16 translate-x-16 opacity-70"></div>
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-sky-500 flex items-center justify-center shadow-md">
                <Smile className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div className="mt-5 inline-block bg-sky-50 text-sky-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                Ages 5 – 16
              </div>
              <h3 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-3">
                Basic Track
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Meet every letter, its sound, and a friendly picture. Bright, playful
                cards make the alphabet come alive.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                  Letters A – Z with sounds
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                  Simple picture words
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                  Tap to hear each sound
                </li>
              </ul>
              <Link
                to="/track/basic"
                data-testid={IDS.tracks.startBasic}
                className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-sky-500 text-white font-bold hover:bg-sky-600 transition-all shadow-md hover:shadow-lg group-hover:-translate-y-0.5"
              >
                Start Basic <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Advanced */}
          <div
            data-testid={IDS.tracks.advancedCard}
            className="group relative bg-white rounded-[2rem] p-8 sm:p-10 border-2 border-slate-100 hover:border-amber-200 transition-all shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 duration-300 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-amber-100 rounded-full -translate-y-16 translate-x-16 opacity-70"></div>
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-md">
                <GraduationCap className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div className="mt-5 inline-block bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                Ages 16 – 25
              </div>
              <h3 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-3">
                Advanced Track
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Master digraphs, blends, and tricky suffixes. Focused literacy
                acquisition for teens and adults building fluency.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Digraphs: SH, CH, TH, PH
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Vowel teams & diphthongs
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Suffix patterns (TION, ING)
                </li>
              </ul>
              <Link
                to="/track/advanced"
                data-testid={IDS.tracks.startAdvanced}
                className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-amber-500 text-white font-bold hover:bg-amber-600 transition-all shadow-md hover:shadow-lg group-hover:-translate-y-0.5"
              >
                Start Advanced <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
